import React, { useState, useEffect } from "react";
import { FiClock, FiGlobe, FiZap } from "react-icons/fi";

/**
 * The current time in UTC, ticking.
 *
 * Every event date is stored and reported in UTC, so the clock those dates are
 * measured against belongs on screen rather than in the head of whoever is
 * reading them. Without it "starts 07:00 UTC" only means something to someone
 * who already knows what time it is in UTC.
 *
 * Two shapes: a `pill` for a page header, and a `banner` for the schedule step,
 * where the clock is the thing being worked against and earns the room. The
 * banner also carries the quick-set shortcuts, since "start now" and "+7 days"
 * are only meaningful next to the clock they are relative to.
 *
 * 24-hour throughout, matching the pickers and tables — one screen showing both
 * a 24h picker and an AM/PM clock is how 19:00 gets read as 07:00.
 */

const LOCAL_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";

const format = (date, timeZone) =>
  date
    .toLocaleString("en-GB", {
      timeZone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(",", "");

const useTickingNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);
  return now;
};

const QUICK_DAYS = [7, 14, 30];

const UtcClock = ({ className = "", variant = "pill", onStartNow, onAddDays, disabled = false }) => {
  const now = useTickingNow();

  if (variant === "pill") {
    return (
      <div
        className={`px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center gap-2 ${className}`}
        title="Current UTC time — event schedules are stored and shown in UTC"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">UTC</span>
        <span className="text-[11px] font-mono font-semibold text-gray-700 tabular-nums">
          {format(now, "UTC")}
        </span>
      </div>
    );
  }

  const showQuickSet = Boolean(onStartNow || onAddDays);

  return (
    <div
      className={`p-4 bg-amber-50/70 border border-amber-200 rounded-2xl flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${className}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-11 h-11 shrink-0 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
          <FiClock size={19} />
        </span>

        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-200/70 rounded-full text-[10px] font-extrabold text-amber-900 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live UTC Clock
          </span>

          <p className="mt-1 text-base font-mono font-bold text-gray-800 tabular-nums truncate">
            {format(now, "UTC")} <span className="text-amber-700">UTC</span>
          </p>

          {/* The same instant on the reader's own clock, so the offset between
              the two is visible rather than something to work out. */}
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5 truncate">
            <FiGlobe size={11} className="shrink-0" />
            Your local time: <span className="font-mono tabular-nums">{format(now, undefined)}</span>
            <span className="text-gray-400">({LOCAL_ZONE})</span>
          </p>
        </div>
      </div>

      {showQuickSet && (
        <div className="flex items-center gap-2 flex-wrap lg:justify-end shrink-0">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Quick set
          </span>

          {onStartNow && (
            <button
              type="button"
              onClick={onStartNow}
              disabled={disabled}
              className="px-3 py-1.5 bg-white border border-amber-300 hover:border-amber-400 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-amber-800 rounded-xl transition flex items-center gap-1.5 shadow-sm"
              title="Set the start to the current UTC minute"
            >
              <FiZap size={13} /> Start Now (UTC)
            </button>
          )}

          {onAddDays &&
            QUICK_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => onAddDays(days)}
                disabled={disabled}
                className="px-3 py-1.5 bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-gray-700 rounded-xl transition shadow-sm"
                title={`End the event ${days} days after it starts`}
              >
                +{days} Days
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default UtcClock;
